import type ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { ExcelNumber } from './excelCells';

type ExportRow = Record<string, unknown>;

const MAX_COLUMN_WIDTH = 50;
const TABLE_MAX_COLUMN_WIDTH = 60;
// Rendered width depends on the viewer's locale, so dates and numbers get a fixed width that fits
// a short date and an amount up to 99,999,999.99. A number that states its own length widens its column
// past that: Excel draws a number too wide for its column as "####".
const DATE_WIDTH = 10;
const NUMBER_WIDTH = 12;

export interface StyledXLSXOptions {
    /** Column headers that get a bold SUM total appended in a final row. */
    sumColumns?: string[];
}

const isHyperlink = (value: unknown): value is ExcelJS.CellHyperlinkValue =>
    typeof value === 'object' && value !== null && 'hyperlink' in value && 'text' in value;

const cellWidth = (value: unknown): number => {
    if (value instanceof Date) {
        return DATE_WIDTH;
    }
    if (value instanceof ExcelNumber) {
        return Math.max(NUMBER_WIDTH, value.width ?? 0);
    }
    if (isHyperlink(value)) {
        return value.text.length;
    }
    return String(value ?? '').length;
};

const addRow = (worksheet: ExcelJS.Worksheet, values: unknown[]): ExcelJS.Row => {
    const row = worksheet.addRow(values.map((value) => (value instanceof ExcelNumber ? value.value : value)));
    values.forEach((value, index) => {
        if (value instanceof ExcelNumber) {
            row.getCell(index + 1).numFmt = value.numFmt;
        }
        // Excel follows a hyperlink cell on click but draws it as plain text unless the cell says otherwise.
        if (isHyperlink(value)) {
            row.getCell(index + 1).font = { underline: true };
        }
    });
    return row;
};

const columnTotal = (data: ExportRow[], header: string): ExcelNumber | null => {
    const cells = data.map((row) => row[header]).filter((value) => value instanceof ExcelNumber);
    const total = cells.reduce((sum, cell) => sum + (cell.value ?? 0), 0);
    return cells.length > 0 ? new ExcelNumber(total, cells[0].numFmt) : null;
};

interface SheetLayout {
    totalRow?: unknown[];
    /** A closing line across the whole last row, in italics. */
    note?: string | null;
    sheetName?: string;
    maxColumnWidth?: number;
    freezeHeader?: boolean;
}

const saveWorkbook = async (
    headers: string[],
    rows: unknown[][],
    fileName: string,
    { totalRow, note, sheetName = 'Data', maxColumnWidth = MAX_COLUMN_WIDTH, freezeHeader = false }: SheetLayout = {}
): Promise<void> => {
    const ExcelJS = (await import('exceljs')).default;
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet(
        sheetName,
        freezeHeader ? { views: [{ state: 'frozen', ySplit: 1 }] } : undefined
    );

    worksheet.columns = headers.map((header, index) => {
        const maxLength = rows.reduce((max, row) => Math.max(max, cellWidth(row[index])), header.length);
        return { header, key: header, width: Math.min(maxLength, maxColumnWidth) + 2 };
    });

    worksheet.getRow(1).font = { bold: true };

    rows.forEach((row) => {
        addRow(worksheet, row);
    });

    if (totalRow) {
        addRow(worksheet, totalRow).font = { bold: true };
    }

    // Left out of the column widths above: merged across the row, it runs over every column.
    if (note) {
        const noteRow = worksheet.addRow([note]);
        noteRow.getCell(1).font = { italic: true };
        if (headers.length > 1) {
            worksheet.mergeCells(noteRow.number, 1, noteRow.number, headers.length);
        }
    }

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    saveAs(blob, fileName);
};

/**
 * Writes the list exports' XLSX with ExcelJS. Cells are typed by value: an `ExcelNumber` becomes a
 * number cell with its format, a `Date` a date cell, a `{ text, hyperlink }` a link, anything else is
 * written as is. Optionally appends a bold SUM total row.
 */
export const generateStyledXLSX = async (
    data: ExportRow[] = [],
    fileName: string,
    options: StyledXLSXOptions = {}
): Promise<void> => {
    const { sumColumns = [] } = options;
    const headers = data.length > 0 ? Object.keys(data[0]) : [];
    const sumHeaders = new Set(sumColumns);

    await saveWorkbook(
        headers,
        data.map((row) => headers.map((header) => row[header])),
        fileName,
        {
            totalRow:
                data.length > 0 && sumHeaders.size > 0
                    ? headers.map((header) => (sumHeaders.has(header) ? columnTotal(data, header) : null))
                    : undefined,
        }
    );
};

/**
 * The workbook of a table read off the page, from rows given by position: its headers may repeat, be empty
 * or read as numbers, and as object keys those would merge or change order. The header row stays in view
 * as the sheet scrolls, columns grow to 60 characters, and a `note` closes the sheet.
 */
export const generateStyledXLSXFromRows = (
    headers: string[],
    rows: unknown[][],
    fileName: string,
    { note = null, sheetName }: { note?: string | null; sheetName?: string } = {}
): Promise<void> =>
    saveWorkbook(headers, rows, fileName, {
        note,
        sheetName,
        maxColumnWidth: TABLE_MAX_COLUMN_WIDTH,
        freezeHeader: true,
    });
